import moment from 'moment-timezone';

import ActionTypes from '@/actions/ActionTypes';
import { add, mergeAt, removeAt } from '@/utils/reducer';

const {
  GROUP__UPDATE_MEMBER,
  GROUP__REMOVE_MEMBER,
  GROUP__REMOVE_MEMBER_AWAITING,
  GROUP__ADD_MEMBER,
  GROUP__UPDATE,
  GROUP__BATCH_ADD,
  GROUP__LIST_LOADING,
  GROUP__LIST_LOADED,
} = ActionTypes;

interface GroupCollection {
  loading: boolean;
  loaded: boolean;
  edges: any[];
  pageInfo: { hasNextPage: boolean };
  resetAt?: string;
  // Which `@include`-gated fields the cached rows were fetched with — see fetchGroups.
  queryKey?: string;
  // The field set of the fetch currently in flight. Kept separate from `queryKey` so
  // invalidating the cached rows' shape can never erase the in-flight marker that stops a
  // re-entrant caller from stampeding.
  loadingQueryKey?: string;
}

const initialCollectionState = {
  loading: false,
  loaded: false,
  edges: [],
  pageInfo: { hasNextPage: true },
  resetAt: undefined,
  queryKey: undefined,
};

interface GroupsState {
  default: GroupCollection;
  all: GroupCollection;
  networks: GroupCollection;
  sharing: GroupCollection;
}

const initialState: GroupsState = {
  default: initialCollectionState,
  all: initialCollectionState,
  networks: initialCollectionState,
  sharing: initialCollectionState,
};

function membersReducer(state = [], action: any) {
  let index;

  switch (action.type) {
    case GROUP__UPDATE_MEMBER:
      // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
      index = state.findIndex((m) => m.node.id === action.groupMemberId);
      if (index < 0) return state;
      return mergeAt(state, index, {
        // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
        node: { ...state[index].node, role: action.role },
      });
    case GROUP__REMOVE_MEMBER:
    case GROUP__REMOVE_MEMBER_AWAITING:
      // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
      index = state.findIndex((m) => m.node.id === action.groupMemberId);
      if (index < 0) return state;
      return removeAt(state, index);
    case GROUP__ADD_MEMBER:
      if (
        state.findIndex((m) => {
          // @ts-expect-error TS(2339): Property 'user' does not exist on type 'never'.
          if (m.user) {
            return (
              // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
              action.member.user && m.node.user.id === action.member.user.id
            );
          }
          // @ts-expect-error TS(2339): Property 'email' does not exist on type 'never'.
          if (m.email) {
            // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
            return m.node.email === action.member.email;
          }
          return false;
        }) >= 0
      )
        return state;

      return [...state, { node: action.member }];
    default:
      return state;
  }
}

function groupReducer(state = {}, action: any) {
  switch (action.type) {
    case GROUP__UPDATE:
      return { ...state, ...action.group };
    case GROUP__UPDATE_MEMBER:
    case GROUP__ADD_MEMBER:
    case GROUP__REMOVE_MEMBER:
      return {
        ...state,
        members: {
          // @ts-expect-error TS(2339): Property 'members' does not exist on type '{}'.
          ...state.members,
          // @ts-expect-error TS(2339): Property 'members' does not exist on type '{}'.
          edges: state.members
            ? // @ts-expect-error TS(2339): Property 'members' does not exist on type '{}'.
              membersReducer(state.members.edges, action)
            : [],
        },
      };
    case GROUP__REMOVE_MEMBER_AWAITING:
      return {
        ...state,
        // @ts-expect-error TS(2339): Property 'members_awaiting' does not exist on type... Remove this comment to see the full error message
        members_awaiting: state.members_awaiting && {
          // @ts-expect-error TS(2339): Property 'members_awaiting' does not exist on type... Remove this comment to see the full error message
          ...state.members_awaiting,
          // @ts-expect-error TS(2339): Property 'members_awaiting' does not exist on type... Remove this comment to see the full error message
          edges: membersReducer(state.members_awaiting.edges, action),
        },
      };
    default:
      return state;
  }
}

function updateNode(state: any, index: any, action: any) {
  const original = state.edges[index];
  const updated = {
    ...original,
    node: groupReducer(original.node, action),
    expiresAt: moment().add(1, 'minute').toISOString(),
    queryKey: action.queryKey,
  };
  return {
    ...state,
    // The merge can overwrite a populated field with null, not just add fields: `billing_account`
    // is selected ungated by GET_GROUP, and the API resolver returns null rather than an error
    // when the request context can't read the account (a public/about-page fetch, say). So the
    // cached rows may no longer carry what the collection's queryKey advertises — drop it and
    // make the next list fetch miss, rather than serve a consumer a field it depends on as null.
    queryKey: undefined,
    edges: mergeAt(state.edges, index, updated),
  };
}

function collectionReducer(state = initialCollectionState, action: any) {
  switch (action.type) {
    case GROUP__UPDATE:
      return (() => {
        const item = action.group;
        // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
        const index = state.edges.findIndex((p) => p.node.id === item.id);
        if (index < 0) {
          return {
            ...state,
            // A single group fetched by id (fetchGroup) carries only the fields that query
            // asked for, which can be fewer than the ones this collection was listed with, so
            // the cached rows no longer match the collection's queryKey — see updateNode.
            queryKey: undefined,
            edges: add(state.edges, {
              node: item,
              expiresAt: moment().add(1, 'minute').toISOString(),
              queryKey: action.queryKey,
            }),
          };
        }
        return updateNode(state, index, action);
      })();
    case GROUP__BATCH_ADD: {
      const { reset, edges, pageInfo, queryKey } = action;
      if (reset) {
        return { edges, pageInfo, resetAt: new Date().toISOString(), queryKey };
      }
      // Spread state so `resetAt` survives pagination: dropping it made `moment(undefined)`
      // evaluate to now, so an appended-to collection could never expire. `force: true` used
      // to paper over that for /teams.
      return { ...state, edges: [...state.edges, ...edges], pageInfo, queryKey };
    }
    case GROUP__LIST_LOADING:
      // The field set travels with the loading flag so a re-entrant identical fetch can
      // recognise this one as already in flight before the rows land (see fetchGroups).
      return { ...state, loading: true, loaded: false, loadingQueryKey: action.queryKey };
    case GROUP__LIST_LOADED:
      return { ...state, loading: false, loaded: true, loadingQueryKey: undefined };
    case GROUP__UPDATE_MEMBER:
    case GROUP__ADD_MEMBER:
    case GROUP__REMOVE_MEMBER:
    case GROUP__REMOVE_MEMBER_AWAITING:
      return (() => {
        const { groupId } = action;
        // @ts-expect-error TS(2339): Property 'node' does not exist on type 'never'.
        const index = state.edges.findIndex((p) => p.node.id === groupId);
        if (index < 0) return state;
        return updateNode(state, index, action);
      })();
    default:
      return state;
  }
}

export default function groupsReducer(state = initialState, action: any): GroupsState {
  switch (action.type) {
    case GROUP__UPDATE:
    case GROUP__UPDATE_MEMBER:
    case GROUP__ADD_MEMBER:
    case GROUP__REMOVE_MEMBER:
    case GROUP__REMOVE_MEMBER_AWAITING: {
      const newState = { ...state };
      Object.keys(state).forEach((col) => {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        newState[col] = collectionReducer(state[col], action);
      });
      return newState;
    }
    case GROUP__BATCH_ADD:
    case GROUP__LIST_LOADING:
    case GROUP__LIST_LOADED: {
      const col = action.collection;
      return {
        ...state,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        [col]: collectionReducer(state[col], action),
      };
    }
    default:
      return state;
  }
}
